import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Tables } from '../services/types/database';
import { apiClient } from '../services/apiClient';
import { useAuth } from './AuthContext';

// Debug configuration - only enable in development with explicit flag
const DEBUG_PROJECT_CONTEXT = import.meta.env.DEV &&
  (typeof localStorage !== 'undefined' && localStorage.getItem('debugProjectContext') === 'true');

// Use the actual database type for projects
type Project = Tables<'projects'>;

export type ProjectContextType = {
  currentProject: Project | null;
  projects: Project[];
  setCurrentProject: (project: Project | null) => void;
  refreshProjects: () => Promise<void>;
  isLoading: boolean;
  /**
   * True once the first project fetch for this user has finished (success or
   * failure). Before that, an empty `projects` list means "not known yet", not
   * "this user has no projects", so empty states must wait for it.
   */
  hasLoaded: boolean;
  error: string | null;
  projectsLimit: number;
  overLimit: boolean;
  needsAssignment: boolean;
};

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectsLimit, setProjectsLimit] = useState(1);
  const [overLimit, setOverLimit] = useState(false);
  const [needsAssignment, setNeedsAssignment] = useState(false);
  const initializationRef = useRef(false);

  /**
   * 🔴 WHY `refreshProjects` AND THE PROVIDER VALUE ARE MEMOISED, AND WHY THESE
   * TWO REFS EXIST.
   *
   * `refreshProjects` used to be a plain async function declared in this
   * component body and handed out through an inline `value={{...}}` literal, so
   * BOTH the function and the value got a new identity on every provider render
   * -- and this component re-renders at least twice per refresh (`setIsLoading`
   * true, then the data). Any consumer that listed `refreshProjects` in an
   * effect's dependency array therefore re-subscribed that effect on every
   * refresh, INCLUDING the refresh it had just triggered itself.
   *
   * `pages/OnboardingWizard.tsx` did exactly that, and its poll effect's cleanup
   * cancels the pending "redirecting to your dashboard" timer. So every
   * successful onboarding entered
   *
   *     poll -> allDone -> await refreshProjects() -> provider render
   *          -> new identity -> effect cleanup CANCELS the armed navigation
   *          -> re-subscribe -> poll -> allDone -> ...
   *
   * measured at 50 arms and 49 cancellations of an armed timer in an eight
   * second window, with completion telemetry fired 50 times and 51 project
   * refreshes, for ONE onboarding. The workaround note in
   * `src/__tests__/phase0/support/wizardMocks.ts` describes this defect as if it
   * were a test-harness concern; it was not.
   *
   * MEMOISING IS SAFE HERE, checked rather than assumed. Every consumer of
   * `refreshProjects` (ProjectsPage, ProjectsSection, OnboardingPage,
   * OnboardingWizard) calls it from an event handler or an effect BODY; none
   * relies on its identity changing. The only reason it could not simply be
   * `useCallback(..., [])` before is that its body READS `currentProject` and
   * `projects`, so an empty dependency array would have frozen those reads.
   * The refs below keep them CURRENT instead -- which is strictly more correct
   * than the closure was: a consumer holding an older `refreshProjects` from a
   * previous render already read a stale `currentProject`.
   */
  const projectsRef = useRef<Project[]>(projects);
  const currentProjectRef = useRef<Project | null>(currentProject);
  useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);
  useEffect(() => {
    currentProjectRef.current = currentProject;
  }, [currentProject]);

  const refreshProjects = useCallback(async () => {
    if (DEBUG_PROJECT_CONTEXT) {
      console.log('🔍 PROJECT CONTEXT DEBUG: Starting refreshProjects()');
      console.log('🔍 CACHE DEBUG: Current projects state before refresh:', {
        projectCount: projectsRef.current.length,
        projects: projectsRef.current.map((p: Project) => ({ id: p.id, name: p.name })),
        timestamp: new Date().toISOString()
      });
    }

    setIsLoading(true);
    setError(null);
    try {
      // 🔧 FIX: Add cache-busting parameters similar to Recent Analysis widget
      const queryParams = new URLSearchParams();
      queryParams.append('_t', Date.now().toString()); // Cache-busting timestamp

      if (DEBUG_PROJECT_CONTEXT) {
        console.log('🔍 CACHE DEBUG: Making API request with cache-busting:', {
          endpoint: `/dashboard/projects?${queryParams.toString()}`,
          timestamp: Date.now()
        });
      }
      
      const response = await apiClient.get(`/dashboard/projects?${queryParams.toString()}`, {
        cache: false,        // Disable caching
        cacheTTL: 0,        // Zero cache TTL
      });
      
      // Handle new response shape: { projects: [...], projects_limit, over_limit, needs_assignment }
      const rawData = response.data?.data?.data || response.data?.data || response.data || {};
      const projectsData = Array.isArray(rawData) ? rawData : (rawData.projects || []);

      // Extract over-limit metadata
      if (!Array.isArray(rawData) && rawData.projects_limit !== undefined) {
        setProjectsLimit(rawData.projects_limit);
        setOverLimit(rawData.over_limit || false);
        setNeedsAssignment(rawData.needs_assignment || false);
      }

      if (DEBUG_PROJECT_CONTEXT) {
        console.log('🔍 PROJECT CONTEXT DEBUG: Fetched projects data:', {
          projectCount: projectsData.length,
          projects: projectsData.map((p: Project) => ({ id: p.id, name: p.name, created_at: p.created_at })),
          timestamp: new Date().toISOString()
        });

        console.log('🔍 CACHE DEBUG: Comparing old vs new project data:', {
          oldCount: projectsRef.current.length,
          newCount: projectsData.length,
          nameChanges: projectsData.map((newP: Project) => {
            const oldP = projectsRef.current.find(p => p.id === newP.id);
            return oldP && oldP.name !== newP.name ?
              { id: newP.id, oldName: oldP.name, newName: newP.name } : null;
          }).filter(Boolean)
        });
      }

      setProjects(projectsData);

      if (DEBUG_PROJECT_CONTEXT) {
        console.log('🔍 STATE DEBUG: ProjectContext state updated - triggering re-render');
      }
      
      // Prefer active (non-locked) projects for auto-selection
      const activeProjects = projectsData.filter((p: Project) => p.status !== 'locked');
      const fallbackProjects = activeProjects.length > 0 ? activeProjects : projectsData;

      // Read through the ref, not a captured render value -- see the note above.
      // Both reads happen in the same synchronous stretch, so they observe the
      // same value the closure would have, and it is the LATEST one.
      const selectedAtRefresh = currentProjectRef.current;

      // If no current project is selected and we have projects, select the first active one
      if (!selectedAtRefresh && projectsData.length > 0) {
        const savedProjectId = localStorage.getItem('rnkrocket-current-project-id');
        const savedProject = savedProjectId
          ? projectsData.find((p: Project) => p.id === savedProjectId && p.status !== 'locked')
          : null;

        const selectedProject = savedProject || fallbackProjects[0];
        if (DEBUG_PROJECT_CONTEXT) {
          console.log('🔍 PROJECT CONTEXT DEBUG: Setting current project:', selectedProject?.name);
        }
        setCurrentProject(selectedProject);
      }

      // If current project is locked or no longer in list, switch to first active
      if (selectedAtRefresh) {
        const currentInList = projectsData.find((p: Project) => p.id === selectedAtRefresh.id);
        if (!currentInList || currentInList.status === 'locked') {
          if (DEBUG_PROJECT_CONTEXT) {
            console.log('🔍 PROJECT CONTEXT DEBUG: Current project locked or removed, selecting new one');
          }
          setCurrentProject(fallbackProjects.length > 0 ? fallbackProjects[0] : null);
        }
      }

      if (DEBUG_PROJECT_CONTEXT) {
        console.log('🔍 PROJECT CONTEXT DEBUG: ProjectContext state updated successfully');
      }
    } catch (err) {
      console.error('❌ ProjectContext: Error fetching projects:', err);
      setError('Failed to load projects');
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, []);

  const handleSetCurrentProject = useCallback((project: Project | null) => {
    setCurrentProject(project);
    if (project) {
      localStorage.setItem('rnkrocket-current-project-id', project.id);
    } else {
      localStorage.removeItem('rnkrocket-current-project-id');
    }
  }, []);

  // Reset initialization when user changes (e.g. logout → login as different user)
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUserId = user?.id ?? null;

    // If user changed (including null → user on login), reset the guard
    if (currentUserId !== prevUserIdRef.current) {
      initializationRef.current = false;
      prevUserIdRef.current = currentUserId;

      // Clear stale projects on logout
      if (!currentUserId) {
        setProjects([]);
        setCurrentProject(null);
        setHasLoaded(false);
        return;
      }
    }

    // Prevent multiple initialization calls (React StrictMode protection)
    if (initializationRef.current) {
      return;
    }

    if (user && !authLoading) {
      initializationRef.current = true;
      if (DEBUG_PROJECT_CONTEXT) {
        console.log('🚀 ProjectProvider: Initializing projects for authenticated user');
      }
      refreshProjects();
    }
  }, [user, authLoading, refreshProjects]);

  /**
   * The value object still changes whenever any piece of STATE changes -- it
   * must, or consumers would never see a new project list. What it no longer
   * does is change when nothing changed, and the two FUNCTIONS it carries are
   * now stable for the life of the provider.
   */
  const value = useMemo<ProjectContextType>(() => ({
    currentProject,
    projects,
    setCurrentProject: handleSetCurrentProject,
    refreshProjects,
    isLoading,
    hasLoaded,
    error,
    projectsLimit,
    overLimit,
    needsAssignment,
  }), [
    currentProject,
    projects,
    handleSetCurrentProject,
    refreshProjects,
    isLoading,
    hasLoaded,
    error,
    projectsLimit,
    overLimit,
    needsAssignment,
  ]);

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProject must be used within ProjectProvider');
  return context;
};